import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'react-toastify';
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid,
  PieChart, Pie, Cell, Legend,
} from 'recharts';
import { FaChartBar, FaChartLine, FaIndustry, FaTrophy, FaPhoneAlt, FaCalendarAlt, FaFileAlt, FaBalanceScale, FaExchangeAlt, FaHistory, FaFileExcel, FaFilePdf, FaSyncAlt } from 'react-icons/fa';
import { crmReportsApi } from '../../api/crmApi';
import './CRM.css';

const PALETTE = ['#F97316', '#6366F1', '#0EA5E9', '#10B981', '#94A3B8', '#EF4444', '#8B5CF6'];

const reports = [
  { key: 'lead-source', title: 'Lead Source Report', icon: <FaChartBar aria-hidden="true" />, desc: 'Leads by source over time' },
  { key: 'india-mart', title: 'IndiaMART Report', icon: <FaIndustry aria-hidden="true" />, desc: 'Synced leads, conversion rate' },
  { key: 'salesperson-performance', title: 'Salesperson Performance', icon: <FaTrophy aria-hidden="true" />, desc: 'Calls, follow-ups, wins per person' },
  { key: 'calls', title: 'Call Report', icon: <FaPhoneAlt aria-hidden="true" />, desc: 'Call outcomes & duration analysis' },
  { key: 'followups', title: 'Follow-up Report', icon: <FaCalendarAlt aria-hidden="true" />, desc: 'Completed vs pending follow-ups' },
  // { key: 'whatsapp', title: 'WhatsApp Report', icon: <FaWhatsapp aria-hidden="true" />, desc: 'Messages sent, delivered, read' },
  // { key: 'opportunities', title: 'Opportunity Report', icon: <FaBullseye aria-hidden="true" />, desc: 'Pipeline value by stage' },
  { key: 'quotation-conversion', title: 'Quotation Conversion', icon: <FaFileAlt aria-hidden="true" />, desc: 'Quotes sent → accepted → SO' },
  { key: 'won-lost', title: 'Won / Lost Report', icon: <FaBalanceScale aria-hidden="true" />, desc: 'Win rate and lost reasons' },
  { key: 'lead-conversion', title: 'Lead Conversion Report', icon: <FaExchangeAlt aria-hidden="true" />, desc: 'Lead → Opportunity → Customer' },
  { key: 'activities', title: 'Activity Report', icon: <FaHistory aria-hidden="true" />, desc: 'Activity volume by type' },
];

const fallbackData = {
  'lead-source': [{ name: 'IndiaMART', value: 42 }, { name: 'Manual', value: 28 }, { name: 'Website', value: 15 }, { name: 'Referral', value: 10 }, { name: 'Other', value: 5 }],
  'india-mart': [{ name: 'Oct', leads: 42, converted: 12 }, { name: 'Sep', leads: 38, converted: 9 }, { name: 'Aug', leads: 31, converted: 8 }],
  'salesperson-performance': [{ name: 'Rahul', calls: 86, followups: 48, won: 7 }, { name: 'Priya', calls: 74, followups: 55, won: 6 }, { name: 'Amit', calls: 68, followups: 42, won: 5 }, { name: 'Sneha', calls: 80, followups: 51, won: 8 }],
  'calls': [{ name: 'Connected', value: 64 }, { name: 'Interested', value: 38 }, { name: 'Call Back', value: 22 }, { name: 'No Answer', value: 18 }, { name: 'Not Interested', value: 14 }],
  'followups': [{ name: 'Completed', value: 72 }, { name: 'Pending', value: 34 }, { name: 'Overdue', value: 9 }],
  'whatsapp': [{ name: 'Sent', value: 410 }, { name: 'Delivered', value: 396 }, { name: 'Read', value: 322 }],
  'opportunities': [{ name: 'New', value: 3 }, { name: 'Qualified', value: 5 }, { name: 'Quotation', value: 4 }, { name: 'Negotiation', value: 6 }, { name: 'Won', value: 8 }],
  'quotation-conversion': [{ name: 'Sent', value: 45 }, { name: 'Accepted', value: 22 }, { name: 'Converted to SO', value: 14 }],
  'won-lost': [{ name: 'Won', value: 26 }, { name: 'Lost', value: 11 }],
  'lead-conversion': [{ name: 'Leads', value: 120 }, { name: 'Opportunities', value: 55 }, { name: 'Customers', value: 24 }],
  'activities': [{ name: 'Calls', value: 308 }, { name: 'WhatsApp', value: 410 }, { name: 'Follow-ups', value: 196 }, { name: 'Meetings', value: 42 }],
};

const fallbackPerf = [
  { name: 'Rahul Sharma', leads: 34, calls: 86, followups: 48, opportunities: 12, won: 7, value: 486000 },
  { name: 'Priya Verma', leads: 30, calls: 74, followups: 55, opportunities: 10, won: 6, value: 402000 },
  { name: 'Amit Kumar', leads: 26, calls: 68, followups: 42, opportunities: 9, won: 5, value: 318000 },
  { name: 'Sneha Patel', leads: 30, calls: 80, followups: 51, opportunities: 11, won: 8, value: 531000 },
];

const money = (v) => `₹${Number(v || 0).toLocaleString('en-IN')}`;

const SkeletonBlock = ({ width = '100%', height = 16, radius = 8, style = {} }) => (
  <span className="crm-skeleton" style={{ width, height, borderRadius: radius, ...style }} />
);

export default function CRMReports() {
  const [active, setActive] = useState('lead-source');
  const [data, setData] = useState(fallbackData['lead-source']);
  const [perf, setPerf] = useState(fallbackPerf);
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [exporting, setExporting] = useState(false);
  const [loading, setLoading] = useState(true);

  const fetchReport = useCallback(async (key) => {
    setLoading(true);
    const setters = {
      'lead-source': () => crmReportsApi.getLeadSourceReport(),
      'india-mart': () => crmReportsApi.getIndiaMartLeadReport(),
      'salesperson-performance': () => crmReportsApi.getSalespersonPerformance(),
      'calls': () => crmReportsApi.getCallReport(),
      'followups': () => crmReportsApi.getFollowupReport(),
      'whatsapp': () => crmReportsApi.getWhatsAppReport(),
      'opportunities': () => crmReportsApi.getOpportunityReport(),
      'quotation-conversion': () => crmReportsApi.getQuotationConversion(),
      'won-lost': () => crmReportsApi.getWonLostReport(),
      'lead-conversion': () => crmReportsApi.getLeadConversionReport(),
      'activities': () => crmReportsApi.getActivityReport(),
    };
    setData(fallbackData[key] || []);
    if (key === 'salesperson-performance') setPerf(fallbackPerf);
    try {
      const params = {};
      if (fromDate) params.fromDate = fromDate;
      if (toDate) params.toDate = toDate;
      const res = await setters[key]();
      if (Array.isArray(res.data) && res.data.length) {
        if (key === 'salesperson-performance') setPerf(res.data);
        else setData(res.data);
      }
    } catch { /* fallback */ } finally { setLoading(false); }
  }, [fromDate, toDate]);

  useEffect(() => { fetchReport(active); }, [active, fetchReport]);

  const exportFile = async (type) => {
    setExporting(true);
    try {
      const fn = type === 'excel' ? crmReportsApi.exportExcel : crmReportsApi.exportPdf;
      const res = await fn(active, { fromDate, toDate });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `crm-report-${active}.${type === 'excel' ? 'xlsx' : 'pdf'}`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success(`Report exported as ${type.toUpperCase()}.`);
    } catch {
      toast.error('Export failed — reports API not available yet.');
    } finally { setExporting(false); }
  };

  const isPie = ['lead-source', 'calls', 'followups', 'quotation-conversion', 'won-lost', 'lead-conversion'].includes(active);
  const meta = reports.find((r) => r.key === active);

  return (
    <div className="crm-page">
      <div className="crm-header">
        <div className="crm-header-left">
          <div className="crm-page-icon"><FaChartLine aria-hidden="true" /></div>
          <div>
            <h1 className="crm-title">CRM Reports</h1>
            <p className="crm-subtitle">Sales analytics — export any report to Excel or PDF</p>
          </div>
        </div>
        <div className="crm-header-actions">
          <input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} className="crm-date-input" title="From date" aria-label="From date" />
          <input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} className="crm-date-input" title="To date" aria-label="To date" />
          <button className="crm-btn crm-btn-outline" onClick={() => exportFile('excel')} disabled={exporting}><FaFileExcel aria-hidden="true" /> Excel</button>
          <button className="crm-btn crm-btn-primary" onClick={() => exportFile('pdf')} disabled={exporting}><FaFilePdf aria-hidden="true" /> PDF</button>
        </div>
      </div>

      <div className="crm-report-layout">
        <div className="crm-report-nav">
          {reports.map((r) => (
            <button key={r.key} className={`crm-report-nav-item ${active === r.key ? 'active' : ''}`} onClick={() => setActive(r.key)}>
              <span className="crm-report-nav-icon">{r.icon}</span>
              <span>
                <strong>{r.title}</strong>
                <small>{r.desc}</small>
              </span>
            </button>
          ))}
        </div>

        <div className="crm-report-content">
          <div className="crm-report-content-head">
            <div>
              <h2 style={{ margin: 0, fontSize: '1.05rem', display: 'flex', alignItems: 'center', gap: 8 }}>{meta?.icon} {meta?.title}</h2>
              <p style={{ margin: '4px 0 0', fontSize: '0.8rem', color: 'var(--ink-soft)' }}>
                {fromDate || toDate ? `${fromDate || '…'} → ${toDate || '…'}` : 'All time'}
              </p>
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <button className="crm-btn crm-btn-sm crm-btn-ghost" onClick={() => fetchReport(active)}><FaSyncAlt aria-hidden="true" /> Refresh</button>
            </div>
          </div>

          {active === 'salesperson-performance' ? (
            <div className="crm-table-wrap" style={{ marginTop: 14 }}>
              <table className="crm-table">
                <thead><tr><th>Salesperson</th><th>Leads</th><th>Calls</th><th>Follow-ups</th><th>Opportunities</th><th>Won</th><th>Won Value</th><th>Win Rate</th></tr></thead>
                <tbody>
                  {loading && Array.from({ length: 4 }).map((_, r) => (
                    <tr key={`skeleton-${r}`}>{Array.from({ length: 8 }).map((__, c) => (
                      <td key={c}><SkeletonBlock width={c === 0 ? 120 : '70%'} height={16} radius={6} /></td>
                    ))}</tr>
                  ))}
                  {!loading && perf.map((p) => (
                    <tr key={p.name}>
                      <td><strong>{p.name}</strong></td>
                      <td>{p.leads ?? 0}</td>
                      <td>{p.calls ?? 0}</td>
                      <td>{p.followups ?? 0}</td>
                      <td>{p.opportunities ?? 0}</td>
                      <td style={{ color: 'var(--success)', fontWeight: 700 }}>{p.won ?? 0}</td>
                      <td style={{ fontWeight: 700 }}>{money(p.value)}</td>
                      <td>{p.opportunities ? Math.round(((p.won || 0) / p.opportunities) * 100) : 0}%</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div style={{ marginTop: 14 }}>
              <div className="crm-chart-card">
                {loading ? (
                  <SkeletonBlock width="100%" height={320} radius={12} />
                ) : (
                <ResponsiveContainer width="100%" height={320}>
                  {isPie ? (
                    <PieChart>
                      <Pie data={data} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={110}
                        label={({ name, value }) => `${name}: ${value}`}>
                        {data.map((_, i) => <Cell key={i} fill={PALETTE[i % PALETTE.length]} />)}
                      </Pie>
                      <Tooltip />
                      <Legend />
                    </PieChart>
                  ) : (
                    <BarChart data={data}>
                      <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
                      <XAxis dataKey="name" tick={{ fontSize: 12, fill: 'var(--ink-soft)' }} />
                      <YAxis tick={{ fontSize: 12, fill: 'var(--ink-soft)' }} />
                      <Tooltip />
                      <Legend />
                      {Object.keys(data[0] || {}).filter((k) => k !== 'name').map((k, i) => (
                        <Bar key={k} dataKey={k} fill={PALETTE[i % PALETTE.length]} radius={[4, 4, 0, 0]} />
                      ))}
                    </BarChart>
                  )}
                </ResponsiveContainer>
                )}
              </div>
            </div>
          )}

          <div className="crm-kpi-grid" style={{ marginTop: 16, gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))' }}>
            {(active === 'salesperson-performance'
              ? [{ label: 'Total Calls', value: perf.reduce((s, p) => s + (p.calls || 0), 0) },
                 { label: 'Total Won', value: perf.reduce((s, p) => s + (p.won || 0), 0) },
                 { label: 'Won Value', value: money(perf.reduce((s, p) => s + (p.value || 0), 0)) }]
              : (data || []).slice(0, 4).map((d) => ({ label: d.name, value: d.value }))
            ).map((k) => (
              <div className="crm-kpi-card" key={k.label}>
                <div><div className="crm-kpi-value">{k.value}</div><div className="crm-kpi-label">{k.label}</div></div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
